// Notifications Settings Dialog Component
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Bell, BellOff, Loader2, CheckCircle2 } from 'lucide-react';
import { 
  isPushSupported, 
  getPermissionStatus, 
  requestPushPermission,
  unsubscribeFromPush,
  initializeFirebase
} from '@/services/pushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationsDialog({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [preferences, setPreferences] = useState({
    dailyReminder: true,      // Rappel quotidien de pesée
    mealReminder: true,       // Rappel des repas
    workoutReminder: true,    // Rappel d'entraînement
    achievements: true,       // Badges et réussites
    weeklySummary: true,      // Résumé hebdomadaire
    community: false          // Activité communautaire (désactivé par défaut)
  });

  useEffect(() => {
    if (open) {
      checkNotificationStatus();
    }
  }, [open]);

  const checkNotificationStatus = async () => {
    const supported = isPushSupported();
    if (!supported) {
      setPermissionStatus('unsupported');
      return;
    }

    const status = getPermissionStatus();
    setPermissionStatus(status);
    setNotificationsEnabled(status === 'granted');

    // Charger les préférences depuis le serveur
    try {
      const response = await fetch(`${API}/notifications/status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNotificationsEnabled(data.enabled);
        if (data.preferences) {
          setPreferences(prev => ({ ...prev, ...data.preferences }));
        }
      }
    } catch (error) {
      console.error('Error fetching notification status:', error);
    }
  };

  const handleEnableNotifications = async () => {
    setLoading(true);
    try {
      // Initialiser Firebase
      const initialized = initializeFirebase();
      if (!initialized) {
        toast.error('Erreur d\'initialisation Firebase');
        setLoading(false);
        return;
      }
      
      // Demander la permission
      const result = await requestPushPermission();
      
      if (result.success) {
        setNotificationsEnabled(true);
        setPermissionStatus('granted');
        
        // Sauvegarder les préférences
        await savePreferences();
        
        toast.success('Notifications activées !', {
          description: 'Vous recevrez des rappels personnalisés.'
        });
      } else {
        if (result.error === 'Permission denied') {
          setPermissionStatus('denied');
          toast.error('Permission refusée', {
            description: 'Activez les notifications dans les paramètres de votre navigateur.'
          });
        } else {
          // Check if permission was actually granted but token failed
          if (getPermissionStatus() === 'granted') {
            setNotificationsEnabled(true);
            setPermissionStatus('granted');
            toast.success('Notifications activées !', {
              description: 'Un problème mineur est survenu mais les notifications sont actives.'
            });
          } else {
            toast.error('Erreur', { description: result.error || 'Erreur inconnue' });
          }
        }
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      // Check permission status anyway
      if (getPermissionStatus() === 'granted') {
        setNotificationsEnabled(true);
        setPermissionStatus('granted');
        toast.success('Notifications activées !');
      } else {
        toast.error('Erreur lors de l\'activation');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setLoading(true);
    try {
      await unsubscribeFromPush();
      setNotificationsEnabled(false);
      toast.success('Notifications désactivées');
    } catch (error) {
      console.error('Error disabling notifications:', error);
      toast.error('Erreur lors de la désactivation');
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    try {
      await fetch(`${API}/notifications/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ preferences })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => {
      const updated = { ...prev, [key]: value };
      // Sauvegarder automatiquement si les notifications sont activées
      if (notificationsEnabled) {
        setTimeout(() => savePreferences(), 500);
      }
      return updated;
    });
  };

  const notificationTypes = [
    {
      key: 'dailyReminder',
      title: 'Rappel de pesée',
      description: 'Rappel quotidien pour enregistrer votre poids',
      icon: '⚖️'
    },
    {
      key: 'mealReminder',
      title: 'Rappels repas',
      description: 'Rappels pour enregistrer vos repas',
      icon: '🍽️'
    },
    {
      key: 'workoutReminder',
      title: 'Entraînements',
      description: 'Rappels pour vos séances de sport',
      icon: '💪'
    },
    {
      key: 'achievements',
      title: 'Badges & Réussites',
      description: 'Notification quand vous débloquez un badge',
      icon: '🏆'
    },
    {
      key: 'weeklySummary',
      title: 'Résumé hebdomadaire',
      description: 'Bilan de vos progrès chaque semaine',
      icon: '📊'
    },
    {
      key: 'community',
      title: 'Communauté',
      description: 'Activité sur vos publications',
      icon: '👥'
    }
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Notifications Push
          </DialogTitle>
          <DialogDescription>
            Recevez des rappels personnalisés pour atteindre vos objectifs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Message informatif si non supporté */}
          {permissionStatus === 'unsupported' && (
            <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                Les notifications push ne sont pas supportées sur ce navigateur.
                Vous pouvez tout de même configurer vos préférences ci-dessous.
              </p>
            </div>
          )}

          {/* Toggle principal - toujours visible */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              {notificationsEnabled ? (
                <Bell className="w-6 h-6 text-primary" />
              ) : (
                <BellOff className="w-6 h-6 text-muted-foreground" />
              )}
              <div>
                <p className="font-medium">
                  {notificationsEnabled ? 'Notifications activées' : 'Notifications désactivées'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {notificationsEnabled ? 'Vous recevez des rappels' : 'Activez pour recevoir des rappels'}
                </p>
              </div>
            </div>
            <Switch
              checked={notificationsEnabled}
              disabled={loading || permissionStatus === 'unsupported'}
              onCheckedChange={(checked) => {
                if (checked) {
                  handleEnableNotifications();
                } else {
                  handleDisableNotifications();
                }
              }}
            />
          </div>

          {/* Préférences de notifications - toujours visibles */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">
              Types de notifications
            </h4>
            {notificationTypes.map((type) => (
              <div 
                key={type.key}
                className="flex items-center justify-between p-3 bg-background rounded-lg border"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{type.icon}</span>
                  <div>
                    <p className="text-sm font-medium">{type.title}</p>
                    <p className="text-xs text-muted-foreground">{type.description}</p>
                  </div>
                </div>
                <Switch
                  checked={preferences[type.key]}
                  onCheckedChange={(checked) => handlePreferenceChange(type.key, checked)}
                />
              </div>
            ))}
          </div>

          {/* Message de consentement */}
          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-xs text-muted-foreground text-center">
              🔒 Vos données sont sécurisées. Nous n'envoyons que des notifications utiles, jamais de spam.
              Vous pouvez désactiver à tout moment.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
          {notificationsEnabled && (
            <Button 
              onClick={async () => {
                setLoading(true);
                try {
                  const response = await fetch(`${API}/notifications/send-test`, {
                    method: 'POST',
                    credentials: 'include'
                  });
                  if (response.ok) {
                    toast.success('Notification de test envoyée !', {
                      description: 'Vérifiez vos notifications dans quelques secondes.'
                    });
                  } else {
                    const data = await response.json().catch(() => ({}));
                    if (data.detail?.includes('No FCM token')) {
                      // Re-request token
                      const result = await requestPushPermission();
                      if (result.success) {
                        // Retry
                        const retryResponse = await fetch(`${API}/notifications/send-test`, {
                          method: 'POST',
                          credentials: 'include'
                        });
                        if (retryResponse.ok) {
                          toast.success('Notification de test envoyée !');
                        } else {
                          toast.error('Impossible d\'envoyer la notification');
                        }
                      } else {
                        toast.error('Token de notification invalide');
                      }
                    } else {
                      toast.error('Erreur lors du test');
                    }
                  }
                } catch (e) {
                  console.error('Test notification error:', e);
                  toast.error('Erreur lors du test');
                }
                setLoading(false);
              }}
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Tester'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
